<template>
    <v-row wrap>
        <v-col cols="6">
            <v-autocomplete
                v-model="selectedHotelKey"
                label="Hotel"
                :items="hotelOptions"
                item-text="text"
                item-value="value"
                autocomplete
                clearable
                outlined
                dense
                hide-details
            ></v-autocomplete>
        </v-col>

        <v-col cols="6">
            <v-autocomplete
                v-model="selectedRoomKey"
                label="Room Type"
                :items="roomOptions"
                item-text="text"
                item-value="value"
                autocomplete
                clearable
                outlined
                dense
                hide-details
                :disabled="!selectedHotelKey"
            ></v-autocomplete>
        </v-col>
    </v-row>
</template>

<script>
/**
 * A standalone hotel + room type picker for use outside of the Dynamic Views
 * form system - e.g. search panels and bulk-edit modals - where the
 * ies-form-field wrapper and its dynamic-forms "field" object are neither
 * needed nor wanted.
 *
 * Unlike inputs/HotelAndRoomKey.vue this component takes explicit props
 * rather than a field object plus a JSON configuration string, and lays
 * itself out as two half-width fields side by side so it fits cleanly
 * whether it is dropped into a full-width row, a narrow fieldset, or a
 * modal column.
 */
export default {
    name: 'ies-hotel-and-room-picker',
    props: {
        /** @var boolean shouldEmitOnSelect - whether selecting a hotel or room type should be announced via the root event bus */
        shouldEmitOnSelect: { type: Boolean, default: false },

        /** @var string selectHotelEventName - the root event bus event name to emit when the hotel selection changes */
        selectHotelEventName: { type: String, default: '' },

        /** @var string selectRoomEventName - the root event bus event name to emit when the room type selection changes */
        selectRoomEventName: { type: String, default: '' },

        /** @var Number|String|null initialHotelKey - a hotel key to pre-select on mount */
        initialHotelKey: { type: [Number, String], default: null },

        /** @var Number|String|null initialRoomKey - a room type key to pre-select on mount */
        initialRoomKey: { type: [Number, String], default: null },
    },
    data() {
        return {
            /** @var [] hotels - all hotels with their room types, as returned by the API */
            hotels: [],

            /** @var Number|String|null selectedHotelKey - the view model for the chosen hotel */
            selectedHotelKey: this.initialHotelKey,

            /** @var Number|String|null selectedRoomKey - the view model for the chosen room type */
            selectedRoomKey: this.initialRoomKey,

            /** @var boolean isInitialising - prevents the hotel watcher from clearing the room type selection while the initial props are still being applied on mount */
            isInitialising: true,
        };
    },
    computed: {
        hotelOptions: function () {
            var hotelOptions = [];
            this.hotels.forEach(function (hotel) {
                hotelOptions.push({ text: '(' + hotel.hotel_code + ') ' + hotel.hotel_name, value: hotel.hotel_key });
            });
            return hotelOptions;
        },

        selectedHotel: function () {
            var selectedHotel = null;
            this.hotels.forEach(function (hotel) {
                if (hotel.hotel_key == this.selectedHotelKey) {
                    selectedHotel = hotel;
                }
            }.bind(this));
            return selectedHotel;
        },

        roomOptions: function () {
            var roomOptions = [];
            if (this.selectedHotel) {
                this.selectedHotel.rooms.forEach(function (room) {
                    roomOptions.push({ text: room.name, value: room.hotel_room_key });
                });
            }
            return roomOptions;
        },
    },
    watch: {
        selectedHotelKey: function (newValue) {
            if (!this.isInitialising) {
                this.selectedRoomKey = null;
            }

            if (this.shouldEmitOnSelect) {
                this.$root.$emit(this.selectHotelEventName, { hotelKey: newValue });
            }
        },

        selectedRoomKey: function (newValue) {
            if (this.shouldEmitOnSelect) {
                this.$root.$emit(this.selectRoomEventName, { hotelKey: this.selectedHotelKey, roomTypeKey: newValue });
            }
        },
    },
    mounted: function () {
        this.loadHotels();
    },
    methods: {
        loadHotels: function () {
            axios.get('/api/hotelsWithRooms').then(function (response) {
                this.hotels = response.data;
                this.isInitialising = false;
            }.bind(this));
        },
    },
};
</script>
